import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProviderProvider, useProvider } from "./contexts/ProviderContext";
import { supabase } from "./lib/supabase";
import { checkSubscriptionStatus } from "./lib/stripe";
import { awaitingSetupFee } from "./lib/plans";
import Login from "./components/Login";
import ShopOnboarding from "./components/ShopOnboarding";
import Dashboard from "./components/Dashboard";
import ProcessorConfig from "./components/ProcessorConfig";
import PaymentPlans from "./components/PaymentPlans";
import ProviderPaymentPlans from "./components/ProviderPaymentPlans";
import AuthCallback from "./components/AuthCallback";
import OAuthWatcher from "./components/OAuthWatcher";
import Header from "./components/Header";
import Providers from "./components/Providers";
import AcceptInvite from "./components/AcceptInvite";
import ProviderCatalog from "./components/provider/ProviderCatalog";
import ProductLines from "./components/ProductLines";
import Analytics from "./components/Analytics";
import ProviderAnalytics from "./components/provider/ProviderAnalytics";
import NewProduct from "./components/provider/NewProduct";
import ProviderSettings from "./components/provider/ProviderSettings";
import ProviderOrders from "./components/provider/ProviderOrders";
import ProviderOrdersList from "./components/provider/ProviderOrdersList";
import ProviderOrderDetails from "./components/provider/ProviderOrderDetails";
import SkuPhotosManager from "./components/provider/SkuPhotosManager";
import ProviderMerchants from "./components/provider/ProviderMerchants";
import MerchantSettings from "./components/MerchantSettings";
import ZalandoIntegration from "./components/ZalandoIntegration";
import TestShop from "./components/TestShop";
import ProviderOnboarding from "./components/ProviderOnboarding";
import { ShopProvider, useShop } from "./contexts/ShopContext";
import { AdminProvider, useAdmin } from "./contexts/AdminContext";
import { ToastProvider } from "./contexts/ToastContext";
import AccessManagement from "./pages/Admin/AccessManagement";
import GlobalBackground from "./components/GlobalBackground";
import { DemoProvider } from "./demo/DemoProvider";
import { isDemoMode } from "./demo/demoMode";
import "./App.css";

// Protected route wrapper for merchant routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { currentShopId } = useShop();
  const [hasShop, setHasShop] = useState<boolean | null>(null);
  const [hasProvider, setHasProvider] = useState<boolean | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  // Created on a plan with a setup fee that is not paid yet: back to onboarding, where it is paid.
  const [awaitingSetup, setAwaitingSetup] = useState(false);
  const [checkingShop, setCheckingShop] = useState(true);
  const demo = isDemoMode();

  useEffect(() => {
    // Demo mode: bypass all auth/subscription checks
    if (demo) {
      setHasShop(true);
      setHasSubscription(true);
      setCheckingShop(false);
      return;
    }

    const checkUserShop = async () => {
      if (!user) {
        setCheckingShop(false);
        return;
      }
      try {
        // Prefer locally selected shop id (from context or localStorage)
        let selectedShopId: string | null = currentShopId;
        try {
          if (!selectedShopId) {
            selectedShopId = localStorage.getItem("flowstock.selectedShopId");
          }
        } catch {}

        // Validate access to selected shop or fallback to most recent mapping
        let shopIdToUse: string | null = null;
        if (selectedShopId) {
          const { data: specific, error: specErr } = await supabase
            .from("auth_users_shops")
            .select("shop_id")
            .eq("auth_user_id", user.id)
            .eq("shop_id", selectedShopId)
            .limit(1);
          if (!specErr && specific && specific.length > 0) {
            shopIdToUse = selectedShopId;
          }
        }
        if (!shopIdToUse) {
          const { data: mappings, error: mapErr } = await supabase
            .from("auth_users_shops")
            .select("shop_id")
            .eq("auth_user_id", user.id)
            .order("last_access_at", { ascending: false })
            .limit(1);
          if (!mapErr && mappings && mappings[0]) {
            shopIdToUse = mappings[0].shop_id;
          }
        }

        if (!shopIdToUse) {
          // User has no shop access, check if they have provider access
          const { data: providerAccess } = await supabase
            .from("auth_users_providers")
            .select("provider_id")
            .eq("auth_user_id", user.id)
            .limit(1);

          if (providerAccess && providerAccess.length > 0) {
            setHasProvider(true);
            setHasShop(false);
            setCheckingShop(false);
            return;
          }

          setHasShop(false);
          setHasProvider(false);
          setCheckingShop(false);
          return;
        }
        setHasShop(true);

        // Prefer unified subscriptions table for gating
        const { data: sub, error: subErr } = await supabase
          .from('subscriptions')
          .select('plan_id, status, current_period_end')
          .eq('merchant_id', shopIdToUse)
          .maybeSingle();

        if (!subErr && sub) {
          const subscriptionStatus = checkSubscriptionStatus(sub);
          setHasSubscription(subscriptionStatus.isActive);
          setAwaitingSetup(awaitingSetupFee(sub));
        } else {
          // Fallback to legacy config_object->subscription if present
          const { data: cfg } = await supabase
            .from('merchant_config')
            .select('config_object')
            .eq('merchant_id', shopIdToUse)
            .single();
          const legacySub = (cfg as any)?.config_object?.subscription;
          if (legacySub) {
            const subscriptionStatus = checkSubscriptionStatus(legacySub);
            setHasSubscription(subscriptionStatus.isActive);
          } else {
            // No subscription found
            setHasSubscription(false);
          }
        }
      } catch (error) {
        setHasShop(false);
      } finally {
        setCheckingShop(false);
      }
    };

    checkUserShop();
  }, [user, currentShopId]);

  if (loading || checkingShop) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  // If user has no shop access but has provider access, redirect to provider catalog
  if (hasShop === false && hasProvider === true) return <Navigate to="/provider/catalog" />;
  if (hasShop === false) return <Navigate to="/onboarding" />;
  if (hasShop === true && hasSubscription === false)
    return <Navigate to={awaitingSetup ? "/onboarding" : "/payment-plans"} />;

  return <>{children}</>;
};

// Protected route wrapper for provider routes
const ProviderProtectedRoute: React.FC<{ children: React.ReactNode; allowSetup?: boolean }> = ({ children, allowSetup = false }) => {
  const { user, loading } = useAuth();
  const { currentProviderId } = useProvider();
  const [hasProvider, setHasProvider] = useState<boolean | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [checkingProvider, setCheckingProvider] = useState(true);
  const demo = isDemoMode();

  useEffect(() => {
    // Demo mode: bypass all auth/subscription checks
    if (demo) {
      setHasProvider(true);
      setHasSubscription(true);
      setCheckingProvider(false);
      return;
    }

    const checkProviderAccess = async () => {
      if (!user) {
        setCheckingProvider(false);
        return;
      }

      try {
        // Get provider ID from context or localStorage
        let providerIdToUse: string | null = currentProviderId;
        if (!providerIdToUse) {
          try {
            providerIdToUse = localStorage.getItem("flowstock.selectedProviderId");
          } catch {}
        }

        // Check if user has provider access
        const { data: providerAccess } = await supabase
          .from("auth_users_providers")
          .select("provider_id")
          .eq("auth_user_id", user.id)
          .limit(1);

        const hasProviderAccess = providerAccess && providerAccess.length > 0;
        setHasProvider(hasProviderAccess);

        if (hasProviderAccess && providerIdToUse) {
          // Check subscription status for provider
          const { data: sub } = await supabase
            .from('subscriptions')
            .select('plan_id, status, current_period_end')
            .eq('provider_id', providerIdToUse)
            .maybeSingle();

          if (sub) {
            const subscriptionStatus = checkSubscriptionStatus(sub);
            setHasSubscription(subscriptionStatus.isActive);
          } else {
            setHasSubscription(false);
          }
        }
      } catch (error) {
        console.error("Error checking provider access:", error);
        setHasProvider(false);
      } finally {
        setCheckingProvider(false);
      }
    };

    checkProviderAccess();
  }, [user, currentProviderId]);

  if (loading || checkingProvider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login?flow=provider" />;
  // Allow access to setup pages even without existing provider
  if (hasProvider === false && !allowSetup) return <Navigate to="/provider/onboarding" />;
  // Check subscription for provider routes (allow setup pages to bypass)
  if (hasProvider === true && hasSubscription === false && !allowSetup)
    return <Navigate to="/provider/payment-plans" />;

  return <>{children}</>;
};

// Landing page wrapper - redirect authenticated users to dashboard
const LandingRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const [hasShop, setHasShop] = useState<boolean | null>(null);
  const [hasProvider, setHasProvider] = useState<boolean | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const demo = isDemoMode();

  useEffect(() => {
    // Demo mode: skip access checks
    if (demo) {
      setHasShop(true);
      setCheckingAccess(false);
      return;
    }

    const checkUserAccess = async () => {
      if (!user) {
        setCheckingAccess(false);
        return;
      }

      try {
        // Check for shop access
        const { data: shopAccess } = await supabase
          .from('auth_users_shops')
          .select('shop_id')
          .eq('auth_user_id', user.id)
          .limit(1);

        setHasShop(shopAccess && shopAccess.length > 0);

        // Check for provider access
        const { data: providerAccess } = await supabase
          .from('auth_users_providers')
          .select('provider_id')
          .eq('auth_user_id', user.id)
          .limit(1);

        setHasProvider(providerAccess && providerAccess.length > 0);
      } catch (error) {
        console.error('Error checking user access:', error);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkUserAccess();
  }, [user]);

  if (loading || (user && checkingAccess)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, redirect based on access type
  if (user) {
    // Provider-only users go to provider catalog
    if (hasProvider && !hasShop) return <Navigate to="/provider/catalog" />;
    // Merchant users (or dual access) go to dashboard
    if (hasShop) return <Navigate to="/dashboard" />;
    // No access at all - this shouldn't happen, but default to dashboard
    return <Navigate to="/dashboard" />;
  }

  // Unauthenticated — send to login
  return <Navigate to="/login" />;
};

// Admin protected route wrapper
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isSuperadmin, loading: adminLoading } = useAdmin();

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center glass-card rounded-2xl p-8">
          <p className="text-red-400 font-heading text-lg mb-2 font-bold">Access Denied</p>
          <p className="text-white/40 text-sm">You don't have superadmin privileges.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <ToastProvider>
        <DemoProvider>
        <ShopProvider>
          <ProviderProvider>
          <AdminProvider>
            <GlobalBackground />
            <div className="App min-h-screen" style={{ position: 'relative', zIndex: 1 }}>
              {/* Global OAuth safety net */}
              <OAuthWatcher />
              <Header />
              <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={<ShopOnboarding />} />
            <Route path="/provider/onboarding" element={<ProviderOnboarding />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/payment-plans" element={<PaymentPlans />} />
            <Route path="/provider/payment-plans" element={<ProviderPaymentPlans />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/processors"
              element={
                <ProtectedRoute>
                  <ProcessorConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/providers"
              element={
                <ProtectedRoute>
                  <Providers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <MerchantSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/test-shop"
              element={
                <ProtectedRoute>
                  <TestShop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zalando"
              element={
                <ProtectedRoute>
                  <ZalandoIntegration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-lines"
              element={
                <ProtectedRoute>
                  <ProductLines />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/providers/:providerId/catalog"
              element={
                <ProtectedRoute>
                  <ProviderCatalog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider/catalog"
              element={
                <ProviderProtectedRoute>
                  <ProviderCatalog />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/catalog/new"
              element={
                <ProviderProtectedRoute>
                  <NewProduct />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/settings"
              element={
                <ProviderProtectedRoute>
                  <ProviderSettings />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/orders"
              element={
                <ProviderProtectedRoute>
                  <ProviderOrders />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/orders/list"
              element={
                <ProviderProtectedRoute>
                  <ProviderOrdersList />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/orders/:orderId"
              element={
                <ProviderProtectedRoute>
                  <ProviderOrderDetails />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/photos"
              element={
                <ProviderProtectedRoute>
                  <SkuPhotosManager />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/analytics"
              element={
                <ProviderProtectedRoute>
                  <ProviderAnalytics />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/provider/merchants"
              element={
                <ProviderProtectedRoute>
                  <ProviderMerchants />
                </ProviderProtectedRoute>
              }
            />
            <Route
              path="/admin/access-management"
              element={
                <AdminProtectedRoute>
                  <AccessManagement />
                </AdminProtectedRoute>
              }
            />
            <Route path="/" element={<LandingRoute />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
            </div>
          </AdminProvider>
          </ProviderProvider>
        </ShopProvider>
        </DemoProvider>
        </ToastProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
